import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { IconArrowLeft, IconCalendarPlus, IconPhone, IconMail, IconAlertTriangle } from "@tabler/icons-react";
import "./patient-detail.scss";
import { useClinicData } from "../../context/ClinicDataContext";
import { FEATURES } from "../../config/features";
import StatusBadge from "../../components/StatusBadge/StatusBadge";
import NewAppointmentModal from "../../components/NewAppointmentModal/NewAppointmentModal";
import RecordPaymentModal from "../../components/RecordPaymentModal/RecordPaymentModal";
import { BASE_PATH } from "../../components/AppLayout/AppLayout";
import { ageFrom, formatDate, formatDateShort, formatIC, formatRM, formatTime, initials } from "../../utils/format";
import { formatPhone, telHref } from "../../utils/phone";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "visits", label: "Visits" },
  { id: "billing", label: "Billing", comingSoon: !FEATURES.billing },
];

const PatientDetailPage = () => {
  const { patientId } = useParams();
  const {
    today,
    patientById,
    appointments,
    invoices,
    apptLabel,
    dentistById,
    itemName,
    invoiceTotal,
    invoiceStatus,
  } = useClinicData();
  const [tab, setTab] = useState("overview");
  const [showBook, setShowBook] = useState(false);
  const [payInvoice, setPayInvoice] = useState(null);

  const patient = patientById[patientId];

  if (!patient) {
    return (
      <div className="panel empty-state">
        <p className="empty-state__title">This patient record doesn't exist.</p>
        <p>
          It may have been removed. <Link to={`${BASE_PATH}/patients`}>Back to patients</Link>
        </p>
      </div>
    );
  }

  const visits = appointments
    .filter((a) => a.patientId === patient.id)
    .sort((x, y) => (y.date + y.start).localeCompare(x.date + x.start));
  const upcoming = visits
    .filter((a) => a.date >= today && !["cancelled", "no-show", "completed"].includes(a.status))
    .reverse();
  const patientInvoices = invoices.filter((inv) => inv.patientId === patient.id);

  return (
    <div className="patient-detail">
      <Link className="patient-detail__back" to={`${BASE_PATH}/patients`}>
        <IconArrowLeft size={18} stroke={1.75} />
        All patients
      </Link>

      <header className="patient-detail__header">
        <span className="avatar avatar--lg">{initials(patient.name)}</span>
        <div className="patient-detail__identity">
          <h2 className="patient-detail__name">{patient.name}</h2>
          <p className="patient-detail__facts">
            <span>
              {ageFrom(patient.dob, today)} years, {patient.gender}
            </span>
            <span>IC {formatIC(patient.ic)}</span>
            <span>Patient since {formatDate(patient.registered)}</span>
          </p>
          {patient.alerts.length > 0 && (
            <div className="patient-detail__alerts">
              <IconAlertTriangle size={16} stroke={2} />
              {patient.alerts.map((alert) => (
                <span className="badge badge--danger" key={alert}>
                  {alert}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="patient-detail__actions">
          <a className="btn btn--secondary" href={telHref(patient.phone)}>
            <IconPhone size={18} stroke={1.75} />
            Call
          </a>
          <button className="btn btn--primary" type="button" onClick={() => setShowBook(true)}>
            <IconCalendarPlus size={18} stroke={1.75} />
            Book appointment
          </button>
        </div>
      </header>

      <div className="patient-detail__tabs tabs" role="tablist" aria-label="Patient record sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            aria-disabled={t.comingSoon || undefined}
            disabled={t.comingSoon}
            className={`tabs__item ${tab === t.id ? "tabs__item--active" : ""} ${t.comingSoon ? "tabs__item--disabled" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {t.comingSoon && <span className="tabs__soon">Coming soon</span>}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="patient-detail__overview">
          <section className="panel">
            <div className="panel__header">
              <h3 className="panel__title">Contact & details</h3>
            </div>
            <dl className="detail-list">
              <div>
                <dt>Mobile</dt>
                <dd>{formatPhone(patient.phone)}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>
                  {patient.email ? (
                    <a href={`mailto:${patient.email}`} className="patient-detail__link">
                      <IconMail size={14} stroke={1.75} /> {patient.email}
                    </a>
                  ) : (
                    "—"
                  )}
                </dd>
              </div>
              <div>
                <dt>Date of birth</dt>
                <dd>{formatDate(patient.dob)}</dd>
              </div>
              <div>
                <dt>Last visit</dt>
                <dd>{formatDate(patient.lastVisit)}</dd>
              </div>
            </dl>
          </section>

          <section className="panel">
            <div className="panel__header">
              <h3 className="panel__title">Upcoming</h3>
            </div>
            {upcoming.length ? (
              <ul className="visit-list">
                {upcoming.map((a) => (
                  <li className="visit-list__item" key={a.id}>
                    <div>
                      <strong>
                        {formatDateShort(a.date)}, {formatTime(a.start)}
                      </strong>
                      <span>
                        {apptLabel(a)} with {dentistById[a.dentistId]?.name}
                      </span>
                    </div>
                    <StatusBadge status={a.status} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="patient-detail__muted">No upcoming appointments.</p>
            )}
          </section>
        </div>
      )}


      {tab === "visits" && (
        <section className="panel panel--flush">
          <div className="data-table__wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Treatment</th>
                  <th>Dentist</th>
                  <th>Notes</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {visits.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <span className="data-table__primary">{formatDate(a.date)}</span>
                      <span className="data-table__secondary">{formatTime(a.start)}</span>
                    </td>
                    <td>{apptLabel(a)}</td>
                    <td>{dentistById[a.dentistId]?.name}</td>
                    <td className="patient-detail__notes">{a.notes || "—"}</td>
                    <td>
                      <StatusBadge status={a.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {visits.length === 0 && (
              <div className="empty-state">
                <p className="empty-state__title">No visits yet</p>
                <p>Book this patient's first appointment to start their history.</p>
              </div>
            )}
          </div>
        </section>
      )}


      {tab === "billing" && FEATURES.billing && (
        <section className="panel panel--flush">
          <div className="data-table__wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Date</th>
                  <th className="data-table__num">Total</th>
                  <th className="data-table__num">Paid</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {patientInvoices.map((inv) => {
                  const status = invoiceStatus(inv);
                  return (
                    <tr key={inv.id}>
                      <td>
                        <span className="data-table__primary">{inv.id}</span>
                        <span className="data-table__secondary">{inv.items.map(itemName).join(", ")}</span>
                      </td>
                      <td className="data-table__nowrap">{formatDate(inv.date)}</td>
                      <td className="data-table__num">{formatRM(invoiceTotal(inv))}</td>
                      <td className="data-table__num">{formatRM(inv.paid)}</td>
                      <td>
                        <StatusBadge status={status} type="invoice" />
                      </td>
                      <td className="data-table__num">
                        {status !== "paid" && (
                          <button className="btn btn--secondary btn--sm" type="button" onClick={() => setPayInvoice(inv)}>
                            Record payment
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {patientInvoices.length === 0 && (
              <div className="empty-state">
                <p className="empty-state__title">No invoices for this patient</p>
              </div>
            )}
          </div>
        </section>
      )}

      {showBook && <NewAppointmentModal defaults={{ patientId: patient.id }} onClose={() => setShowBook(false)} />}
      {payInvoice && <RecordPaymentModal invoice={payInvoice} onClose={() => setPayInvoice(null)} />}
    </div>
  );
};

export default PatientDetailPage;
